import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler';

const Beds = () => {
  const navigate = useNavigate();

  const [beds, setBeds] = useState([]);
  const [properties, setProperties] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);

  const [isEdit, setIsEdit] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [filterPropertyId, setFilterPropertyId] = useState('');
  const [filterFloorId, setFilterFloorId] = useState('');

  const [form, setForm] = useState({
    id: "",
    property_id: "",
    floor_id: "",
    room_id: "",
    bedtype: "",
    bed_no: "",
    rate: ""
  });

  useEffect(() => {
    getBeds();
    getProperties();
    getBedTypes();
  }, []);

  useEffect(() => {
    if (filterPropertyId) {
      getFloors(filterPropertyId);
    } else {
      setFloors([]);
      setFilterFloorId('');
    }
  }, [filterPropertyId]);

  const getBeds = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-beds`, { withCredentials: true });
      if (res.data.status) setBeds(res.data.beds || []);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const getProperties = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-properties`, { withCredentials: true });
      if (res.data.status) setProperties(res.data.properties || []);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const getFloors = async (propertyId) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-floors?property_id=${propertyId}`, { withCredentials: true });
      if (res.data.status) setFloors(res.data.floors || []);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const getRooms = async (floorId) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-rooms?floor_id=${floorId}`, { withCredentials: true });
      if (res.data.status) setRooms(res.data.rooms || []);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const getBedTypes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-bedtypes`, { withCredentials: true });
      if (res.data.status) setBedTypes(res.data.bedtypes || []);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const openAddModal = () => {
    setForm({ id: "", property_id: "", floor_id: "", room_id: "", bedtype: "", bed_no: "", rate: "" });
    setFloors([]);
    setRooms([]);
    setIsEdit(false);
    setShowModal(true);
  };

  const openEditModal = async (bed) => {
    setForm(bed);
    await getFloors(bed.property_id);
    await getRooms(bed.floor_id);
    setIsEdit(true);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    if (name === "property_id") {
      setForm(prev => ({ ...prev, floor_id: "", room_id: "" }));
      setFloors([]);
      setRooms([]);
      getFloors(value);
    } else if (name === "floor_id") {
      setForm(prev => ({ ...prev, room_id: "" }));
      setRooms([]);
      getRooms(value);
    }
  };

  const validateBedForm = () => {
    const { property_id, floor_id, room_id, bedtype, bed_no, rate } = form;
    if (!property_id || !floor_id || !room_id || !bedtype || !bed_no || rate === undefined || rate === '') {
      Swal.fire({ icon: 'warning', title: 'Validation Error', text: 'Please fill all required fields.' });
      return false;
    }
    if (isNaN(rate) || Number(rate) < 0) {
      Swal.fire({ icon: 'warning', title: 'Invalid Rate', text: 'Rate must be a valid non-negative number.' });
      return false;
    }
    return true;
  };

  const saveBed = async () => {
    try {
      if (!validateBedForm()) return;
      await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/create-bed`, form, { withCredentials: true });
      Swal.fire("Success", "Bed added!", "success");
      getBeds();
      setShowModal(false);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const updateBed = async () => {
    try {
      if (!validateBedForm()) return;
      await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/update-bed`, form, { withCredentials: true });
      Swal.fire("Success", "Bed updated!", "success");
      getBeds();
      setShowModal(false);
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  const deleteBed = async (id) => {
    const confirm = await Swal.fire({ title: "Confirm Delete", icon: "warning", showCancelButton: true });
    if (!confirm.isConfirmed) return;

    try {
      await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/admin/delete-bed/`, { id }, { withCredentials: true });
      Swal.fire("Success", "Bed deleted!", "success");
      getBeds();
    } catch (error) {
      globalErrorHandler(error, navigate);
    }
  };

  return (
    <div className="container-fluid px-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 flex-grow-1">Beds</h3>
        <div className="d-flex align-items-center">
          <select className="form-select me-2" style={{ minWidth: "200px" }} value={filterPropertyId} onChange={(e) => {
            setFilterPropertyId(e.target.value);
            setFilterFloorId("");
          }}>
            <option value="">All Properties</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.property_name}</option>)}
          </select>

          <select className="form-select me-2" style={{ minWidth: "200px" }} value={filterFloorId} onChange={(e) => setFilterFloorId(e.target.value)} disabled={!filterPropertyId}>
            <option value="">All Floors</option>
            {floors.filter(f => f.property_id == filterPropertyId).map(f => (
              <option key={f.id} value={f.id}>{f.floor_name}</option>
            ))}
          </select>
        </div>
        <Button variant="success" onClick={openAddModal}>Add New Bed</Button>
      </div>

      <table className="table table-bordered table-hover shadow-sm">
        <thead className="table-success">
          <tr>
            <th>Bed No</th>
            <th>Property</th>
            <th>Floor</th>
            <th>Room</th>
            <th>Bed Type</th>
            <th>Rate</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {beds.length === 0 ? (
            <tr><td colSpan="7">No records found</td></tr>
          ) : (
            beds
              .filter(b => (!filterPropertyId || b.property_id == filterPropertyId) && (!filterFloorId || b.floor_id == filterFloorId))
              .map(bed => (
                <tr key={bed.id}>
                  <td>{bed.bed_no}</td>
                  <td>{bed.property_name}</td>
                  <td>{bed.floor_name}</td>
                  <td>{bed.room_no}</td>
                  <td>{bed.bedtypename}</td>
                  <td>{bed.rate}</td>
                  <td>
                    <Button variant="info" size="sm" className="me-2" onClick={() => openEditModal(bed)}>Edit</Button>
                    <Button variant="danger" size="sm" onClick={() => deleteBed(bed.id)}>Delete</Button>
                  </td>
                </tr>
              ))
          )}
        </tbody>
      </table>

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Bed" : "Add Bed"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <label className="form-label">Bed No</label>
            <input type="text" className="form-control" value={form.bed_no || ""} maxLength={255} onChange={(e) => setForm(prev => ({ ...prev, bed_no: e.target.value }))} />
          </div>
          <div className="mb-2">
            <label className="form-label">Property</label>
            <select className="form-select" name="property_id" value={form.property_id} onChange={handleChange}>
              <option value="">Select Property</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.property_name}</option>)}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label">Floor</label>
            <select className="form-select" name="floor_id" value={form.floor_id} onChange={handleChange}>
              <option value="">Select Floor</option>
              {floors.map(f => <option key={f.id} value={f.id}>{f.floor_name}</option>)}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label">Room</label>
            <select className="form-select" name="room_id" value={form.room_id} onChange={handleChange}>
              <option value="">Select Room</option>
              {rooms.map(r => <option key={r.id} value={r.id}>{r.room_no}</option>)}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label">Bed Type</label>
            <select className="form-select" name="bedtype" value={form.bedtype} onChange={handleChange}>
              <option value="">Select Bed Type</option>
              {bedTypes.map(bt => <option key={bt.id} value={bt.id}>{bt.bedtype}</option>)}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label">Rate</label>
            <input type="number" className="form-control" name="rate" value={form.rate} onChange={handleChange} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={isEdit ? updateBed : saveBed}>{isEdit ? "Update" : "Save"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Beds;
